import {put, call} from 'redux-saga/effects';
import TemplateTask from "../../entities/templateTask";
import * as Actions from "../actions/templateTaskActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type TemplateTaskAction = {
    templateTaskId: string;
    templateTask: TemplateTask;
    templateTasks: TemplateTask[];
};

export function* fetchOneTemplateTask(action: TemplateTaskAction) {
	try {
		const templateTask: TemplateTask = yield call(api.findOne, 'templateTask', action.templateTaskId);

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_FETCH_ONE_SUCCESS, templateTaskId: action.templateTaskId, templateTask});
	}
	catch(err) {
		yield put({type: Actions.TEMPLATETASK_FETCH_ONE_FAILURE, templateTaskId: action.templateTaskId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {TemplateTaskAction} action
 */
export function* saveTemplateTask(action: TemplateTaskAction) {
    // console.debug('⮂ TEMPLATETASK_SAVE saga', action.templateTask?.id);
    
	if (action.templateTask === undefined || action.templateTask === null) {
		console.error('saveTemplateTask received a null');
		throw new Error('saveTemplateTask received a null');
	}

	if (!(action.templateTask instanceof TemplateTask)) {
		action.templateTask = new TemplateTask(action.templateTask);
	}

	try {
		// Save to IndexedDB
		const addResult = yield call(idb.add, 'templateTask', action.templateTask);
		if (addResult.isErr()) {
			console.error(`⮂ saveTemplateTask: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TEMPLATETASK_SAVE_FAILURE, templateTask: action.templateTask, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_SAVE, templateTask: action.templateTask});

		// Delete this entry from the cache (if there is one). Use uncacheByEntityId so we
		// catch entries regardless of which parentId the writer used (zero for grid views,
		// topLevelId for sub-task views, etc.) — the prior uncacheItem call passed entityId
		// as parentId, which never matched what writers store under, so entries leaked.
		yield call(window.storageWorker.uncacheByEntityId, getCurrentFirmId(), getCurrentUserId(), 'templateTask', action.templateTask.id);

		// Let the queue know to process
		yield call(processQueue);
	}
	catch(err) {
		yield put({type: Actions.TEMPLATETASK_SAVE_FAILURE, templateTask: action.templateTask, status: 500, err});

		console.error('Error saving to database', err);
	}
}

/**
 * TODO: Update for saveMany
 * @param {TemplateTaskAction} action
 * @param {TemplateTask[]} action.templateTasks
 */
export function* saveManyTemplateTasks(action: TemplateTaskAction) {
    // console.debug('⮂ TEMPLATETASK_SAVE_MANY saga', action.templateTasks.map(templateTask => templateTask.id).join(','));
    
    if (!Array.isArray(action.templateTasks)) {
        console.error('saveManyTemplateTasks expected an array, received', action.templateTasks);
        throw new Error('saveManyTemplateTasks expected an array');
    }

	try {
		const toSave: TemplateTask[] = [];

		// Save each item to IndexedDB
		for(let i = 0; i < action.templateTasks.length; i++) {
			const templateTask = action.templateTasks[i];
			templateTask.persisted = false;
			toSave.push(templateTask);
		}

		const addResult = yield call(idb.add, 'templateTask', toSave);
		if (addResult.isErr()) {
			console.error(`⮂ saveManyTemplateTasks: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TEMPLATETASK_SAVE_MANY_FAILURE, templateTasks: action.templateTasks, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_SAVE_MANY, templateTasks: toSave});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.TEMPLATETASK_SAVE_MANY_FAILURE, templateTasks: action.templateTasks, status: 500, err});

		console.error(`Error saving ${action.templateTasks.length} templateTasks to database`, err);
	}
}

/**
 * @param {TemplateTaskAction} action
 */
export function* removeTemplateTask(action: TemplateTaskAction) {
    const userId = getCurrentUserId();
	if (!userId || action.templateTask === undefined || action.templateTask === null || !(action.templateTask instanceof TemplateTask)) {
			console.error('removeTemplateTask expected type TemplateTask, received', action.templateTask);
			throw new Error('removeTemplateTask expected type TemplateTask');
		}

	try {
		// Mark as deleted
		action.templateTask.status = 'D';
		action.templateTask.addedOn = newHRTimestamp();
		action.templateTask.addedBy = userId;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'templateTask', action.templateTask);
		if (addResult.isErr()) {
			console.error(`⮂ removeTemplateTask: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TEMPLATETASK_REMOVE_FAILURE, templateTask: action.templateTask, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_REMOVE, templateTask: action.templateTask});

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		yield put({type: Actions.TEMPLATETASK_REMOVE_FAILURE, templateTask: action.templateTask, status: 500, err});

		console.error('Error deleting from database', err);
	}
}

/**
	* Restore the last version of this TemplateTask.
	*
	* @param {TemplateTaskAction} action
	*/
export function* restoreTemplateTask(action: TemplateTaskAction) {
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreTemplateTask expected getCurrentUserId() to return a value');
        return;
    }
    
	if (action.templateTask === undefined || action.templateTask === null) {
        console.error('restoreTemplateTask expected a TemplateTask, received null');
        throw new Error('restoreTemplateTask expected a TemplateTask, received null');
    }
    else if (!(action.templateTask instanceof TemplateTask)) action.templateTask = new TemplateTask(action.templateTask); 
    
	try {
		// Mark as restored
		action.templateTask.status = 'U';
		action.templateTask.addedOn = newHRTimestamp();
		action.templateTask.addedBy = userId;
		action.templateTask.restore = true;

		// Save to IndexedDB
		const addResult = yield call(idb.add, 'templateTask', action.templateTask);
		if (addResult.isErr()) {
			console.error(`⮂ restoreTemplateTask: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.TEMPLATETASK_RESTORE_FAILURE, templateTask: action.templateTask, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.TEMPLATETASK_RESTORE, templateTask: action.templateTask});
	}
	catch(err) {
		yield put({type: Actions.TEMPLATETASK_RESTORE_FAILURE, templateTask: action.templateTask, status: 500, err});

		console.error('Error restoring from database', err);
	}
}